import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { MOBIL_MAX_PX } from '@/hooks/useLayout';
import type { TourAktion, TourErwartung, TourSchritt } from './tour';

// Die Mechanik der Tour. Was gezeigt wird, steht in tour.ts.
//
// driver.js übernimmt Scheinwerfer, Sprechblase und Knöpfe. Die Abfolge
// steuert diese Datei selbst, weil vor manchen Schritten etwas passieren
// muss (eine Übungsbestellung anlegen, zu einer Seite wechseln) und
// driver.js dafür nur synchrone Haken anbietet.

const WARTE_AUF_ANKER_MS = 4000;
const PULS_MS = 300;
/** Dauer der eigenen Einblendung der Sprechblase (siehe index.css). */
const EINBLENDEN_MS = 180;
/**
 * Ruhefenster nach dem Erscheinen. Solange darf nichts nachgeführt
 * werden — erst wenn die Sprechblase steht, ist der Beobachter scharf.
 */
const RUHEFENSTER_MS = EINBLENDEN_MS + 120;

/**
 * Wo die Sprechblase steht — angepasst an die Fensterbreite.
 *
 * Die Seitenangaben in tour.ts sind für den Schreibtisch gedacht: dort
 * ist neben einer Tabellenspalte oder der Seitenspalte Platz für eine
 * Blase. Auf 390 Pixeln ist er das nicht — die Blase ist selbst schon
 * 352 Pixel breit, „links daneben" gäbe es nicht. driver.js sucht sich
 * dann notgedrungen selbst eine Stelle, und die war beim Durchspielen
 * mal über dem Ziel, mal halb aus dem Bild.
 *
 * Deshalb hier die Übersetzung: was links oder rechts stehen sollte,
 * steht am Telefon darunter. Reicht auch dort der Platz nicht — etwa bei
 * einem Anker in der Bottom-Navigation —, klappt driver.js von selbst
 * nach oben; dieser Fall ist ihm im Gegensatz zum seitlichen geläufig.
 *
 * Die Breite wird einmal beim Start gelesen. Wer das Gerät mitten in der
 * Tour dreht, bekommt die Seiten des alten Formats; ein neuer Anlauf
 * richtet es. Das in Kauf zu nehmen ist billiger, als die Schrittliste
 * bei jedem Drehen neu aufzubauen.
 */
export function seiteFuersFenster(
  seite: TourSchritt['seite']
): TourSchritt['seite'] {
  if (seite === undefined) return undefined;
  if (window.innerWidth > MOBIL_MAX_PX) return seite;
  return seite === 'left' || seite === 'right' ? 'bottom' : seite;
}

export type TourUmgebung = {
  /** Führt eine Aktion aus, bevor der zugehörige Schritt gezeigt wird. */
  aktion: (was: TourAktion) => Promise<void>;
  /** Aktueller Pfad — für die Erwartung "route-enthaelt". */
  pfad: () => string;
  /** Fragt vor dem Abbruch nach. Liefert false, läuft die Tour weiter. */
  abbruchBestaetigen: () => Promise<boolean>;
  /** Wird am Ende gerufen: durchgelaufen (true) oder abgebrochen (false). */
  fertig: (durchgelaufen: boolean) => void;
};

/**
 * Fortschritt in einer Zeile: "Erfassung üben · 4/7".
 *
 * Vorher stand hier "Kapitel 2 von 4 · Erfassung üben — Schritt 4 von 7".
 * Das war vollständig und brach in der schmalen Sprechblase über drei
 * Zeilen um — die Fußzeile wog damit schwerer als der Text darüber. Die
 * Kapitelnummer fällt weg: der Name sagt schon, wo man ist, und wie viele
 * Kapitel es insgesamt gibt, steht im Hilfe-Panel.
 */
function fortschrittstext(schritte: TourSchritt[], i: number): string {
  const s = schritte[i];
  if (s.kapitelTitel === undefined) return `${i + 1}/${schritte.length}`;
  const gleiche = schritte.filter((x) => x.kapitelTitel === s.kapitelTitel);
  return `${s.kapitelTitel} · ${gleiche.indexOf(s) + 1}/${gleiche.length}`;
}

/**
 * Kopiert die nackten Ziffern einer ISBN aus der Sprechblase.
 *
 * Angezeigt wird die Nummer mit Strichen wie überall sonst; in der
 * Zwischenablage landen die Ziffern ohne. Der Grund steht in lib/isbn.ts:
 * Anzeige und Datenfluss sind zwei verschiedene Dinge.
 */
async function isbnKopieren(knopf: HTMLElement): Promise<void> {
  const nackt = knopf.dataset.isbnKopieren ?? '';
  if (nackt === '') return;
  let geklappt = false;
  try {
    await navigator.clipboard.writeText(nackt);
    geklappt = true;
  } catch {
    // Ohne sicheren Kontext oder ohne Erlaubnis gibt es die
    // Zwischenablage-API nicht. Der alte Weg über ein unsichtbares Feld
    // funktioniert dort weiterhin — lieber das als gar nichts.
    geklappt = kopiereUeberFeld(nackt);
  }
  knopf.dataset.kopiert = geklappt ? 'ja' : 'nein';
  window.setTimeout(() => delete knopf.dataset.kopiert, 1400);
}

/**
 * Beschriftung des Hauptknopfes einer Karte.
 *
 * Drei Fälle, in dieser Reihenfolge: letzte Karte schließt ab, eine
 * Karte mit Erwartung lässt sich überspringen, alles andere führt
 * weiter.
 */
function letzterKnopf(s: TourSchritt, istLetzte: boolean): string {
  if (istLetzte) return 'Fertig';
  return s.erwartung !== undefined ? 'Überspringen' : 'Weiter';
}

function kopiereUeberFeld(text: string): boolean {
  const feld = document.createElement('textarea');
  feld.value = text;
  feld.setAttribute('readonly', '');
  feld.style.position = 'fixed';
  feld.style.opacity = '0';
  document.body.appendChild(feld);
  feld.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  feld.remove();
  return ok;
}

export function ankerElement(anker: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${anker}"]`);
}

function warteAufAnker(anker: string, msMax: number): Promise<HTMLElement | null> {
  const bis = Date.now() + msMax;
  return new Promise((fertig) => {
    const sehen = (): void => {
      const el = ankerElement(anker);
      if (el !== null || Date.now() > bis) {
        fertig(el);
        return;
      }
      window.setTimeout(sehen, 100);
    };
    sehen();
  });
}

/** Platz, den ein aufklappendes Menue ueber dem Ziel braucht. */
const ABSTAND_OBEN = 220;

/**
 * Scrollt das Ziel in einen bequemen Bereich und wartet, bis die Seite
 * wieder steht.
 */
async function scrolleHeran(el: HTMLElement | null): Promise<void> {
  if (el === null) return;
  const r = el.getBoundingClientRect();
  const zuHoch = r.top < ABSTAND_OBEN;
  const zuTief = r.bottom > window.innerHeight - 160;
  if (zuHoch || zuTief) {
    const ziel = window.scrollY + r.top - ABSTAND_OBEN;
    window.scrollTo({ top: Math.max(0, ziel), behavior: 'smooth' });
    await ruhe();
  }
}

/**
 * Steht der Schreibcursor gerade in einem Eingabefeld innerhalb des
 * markierten Bereichs?
 *
 * Nur dann darf nicht nachgeführt werden — refresh() zieht den Fokus in
 * die Sprechblase und würde einen halb getippten Satz abschneiden.
 * Beim ersten Anlauf galt hier JEDES fokussierte Element, auch ein
 * gerade gedrückter Knopf. Damit blieb der Ausschnitt genau dann stehen,
 * wenn ein Klick die Seite verändert hatte — also im wichtigsten Fall
 * (B91).
 */
function wirdGetippt(bereich: Element): boolean {
  const el = document.activeElement;
  if (el === null || !bereich.contains(el)) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  if (el instanceof HTMLInputElement) {
    return ['text', 'search', 'tel', 'email', 'url', 'number', 'password'].includes(el.type);
  }
  return false;
}

/**
 * Lage eines Elements als grober Vergleichswert.
 *
 * Auf vier Pixel gerundet, damit ein Sub-Pixel-Zittern (Scroll-Ausklang,
 * gerundete Zoomstufen) nicht als Verschiebung durchgeht und eine
 * Nachführung auslöst, die nichts nachzuführen hat.
 */
function lageVon(el: Element | null | undefined): string {
  if (el === null || el === undefined) return '';
  const r = el.getBoundingClientRect();
  return [r.x, r.y, r.width, r.height].map((n) => Math.round(n / 4)).join(',');
}

/** Wartet zwei Bilder — dann steht eine frisch gesetzte Position im Layout. */
function zweiBilder(): Promise<void> {
  return new Promise((fertig) => {
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => fertig()));
  });
}

/** Wartet, bis sich die Scrollposition zwei Bilder lang nicht ändert. */
function ruhe(msMax = 1200): Promise<void> {
  return new Promise((fertig) => {
    const bis = Date.now() + msMax;
    let letzte = -1;
    let gleich = 0;
    const sehen = (): void => {
      const jetzt = Math.round(window.scrollY);
      gleich = jetzt === letzte ? gleich + 1 : 0;
      letzte = jetzt;
      if (gleich >= 2 || Date.now() > bis) {
        fertig();
        return;
      }
      window.requestAnimationFrame(sehen);
    };
    window.requestAnimationFrame(sehen);
  });
}

function erwartungErfuellt(e: TourErwartung, pfad: string): boolean {
  switch (e.art) {
    case 'anker-da':
      return ankerElement(e.anker) !== null;
    case 'anker-weg':
      return ankerElement(e.anker) === null;
    case 'route-enthaelt':
      return pfad.includes(e.teil);
    case 'text-in-anker': {
      const el = ankerElement(e.anker);
      return el !== null && (el.textContent ?? '').includes(e.enthaelt);
    }
    case 'gedrueckt': {
      const el = ankerElement(e.anker);
      if (el === null) return false;
      const schalter = Array.from(el.querySelectorAll('[aria-pressed]')).find((s) =>
        (s.textContent ?? '').includes(e.beschriftung)
      );
      return schalter?.getAttribute('aria-pressed') === 'true';
    }
  }
}

/**
 * Startet eine Folge von Schritten. Rückgabe bricht sie ab.
 */
export function starteLauf(schritte: TourSchritt[], umgebung: TourUmgebung): () => void {
  let index = 0;
  let beendet = false;
  let puls: number | undefined;
  let beobachter: ResizeObserver | null = null;
  let fuehrer: Driver | null = null;
  let letzteLage = '';
  let ruheBis = 0;
  /** Läuft gerade ein Schrittwechsel? Nur dann darf versteckt werden. */
  let imWechsel = false;

  // Der Chip in der Sprechblase wird von driver.js gerendert, nicht von
  // React — deshalb ein delegierter Lauscher statt onClick. Er haengt am
  // Dokument, weil die Blase bei jedem Schrittwechsel neu entsteht.
  const chipKlick = (e: MouseEvent): void => {
    const ziel = e.target;
    if (!(ziel instanceof Element)) return;
    const knopf = ziel.closest<HTMLElement>('[data-isbn-kopieren]');
    if (knopf === null) return;
    e.preventDefault();
    void isbnKopieren(knopf);
  };
  document.addEventListener('click', chipKlick);

  function pulsStoppen(): void {
    if (puls !== undefined) {
      window.clearInterval(puls);
      puls = undefined;
    }
    beobachter?.disconnect();
    beobachter = null;
  }

  function beenden(durchgelaufen: boolean): void {
    if (beendet) return;
    beendet = true;
    imWechsel = false;
    document.removeEventListener('click', chipKlick);
    pulsStoppen();
    fuehrer?.destroy();
    fuehrer = null;
    umgebung.fertig(durchgelaufen);
  }

  // Die Sprechblasen brauchen die Schritte als driver.js-Liste, damit
  // Fortschritt ("3 von 12") und Knöpfe stimmen. Gezeigt wird immer nur
  // der Schritt, den gehZu() freigibt.
  /**
   * Versteckt die Sprechblase, sobald driver.js sie neu aufbaut.
   *
   * driver.js hängt die Blase erst an den Body und positioniert sie
   * danach. Wer in diesem Moment hinsieht, sieht sie kurz an der
   * falschen Stelle. Sie startet deshalb unsichtbar; sichtbar macht sie
   * blaseZeigen(), wenn sie fertig platziert ist.
   *
   * Der Haken läuft NICHT nur beim Schrittwechsel: driver.js ruft ihn
   * auch bei jedem refresh() — also jedes Mal, wenn der Scheinwerfer dem
   * Element nachgeführt wird. Ohne die Schranke unten bliebe die Blase
   * nach so einem Nachführen für immer unsichtbar; im aufgezeichneten
   * Bildschirm war sie eine halbe Sekunde nach dem Wechsel spurlos weg.
   */
  const blaseAufbereiten = (popover: { wrapper: HTMLElement }): void => {
    // Auf der letzten Karte bleibt genau ein Knopf stehen.
    //
    // Zwei Wege dorthin haben im Build NICHT funktioniert und stehen
    // hier als Warnung: driver.js' `showButtons` auf Schritt-Ebene
    // ueberschreibt die globale Liste nicht, und die Knoepfe hier per
    // style.display wegzunehmen verpufft — driver.js setzt ihre
    // Sichtbarkeit NACH diesem Haken. Es bleibt die Klasse, gegen die
    // CSS mit !important gewinnt, egal wie oft neu gerendert wird.
    popover.wrapper.classList.toggle('engel-tour-letzte', index === schritte.length - 1);

    if (!imWechsel) return;
    popover.wrapper.classList.add('engel-tour-verborgen');
  };

  function blaseZeigen(): void {
    imWechsel = false;
    document
      .querySelectorAll('.engel-tour-verborgen')
      .forEach((el) => el.classList.remove('engel-tour-verborgen'));
  }

  const driverSchritte: DriveStep[] = schritte.map((s, i) => ({
    // Der Rückfall auf <body> kommt praktisch nie vor: gehZu() zeigt einen
    // Schritt erst, wenn sein Anker wirklich da ist. Verschwindet er in
    // der Millisekunde danach, leuchtet eben die Seite statt eines
    // Ausschnitts — besser als ein Absturz.
    element:
      s.anker === undefined ? undefined : () => ankerElement(s.anker as string) ?? document.body,
    popover: {
      title: s.titel,
      description: s.text,
      side: seiteFuersFenster(s.seite),
      align: 'start',
      progressText: fortschrittstext(schritte, i),
      onPopoverRender: blaseAufbereiten,
      // Auf Schritten, die etwas verlangen, heisst der Knopf nicht
      // "Weiter" — man kommt ohnehin von selbst weiter, sobald man es
      // getan hat. Wer ihn drueckt, ueberspringt die Uebung (B105).
      //
      // Auf der letzten Karte heisst er "Fertig" und steht allein da.
      // "Weiter" waere ein Versprechen auf eine Karte, die es nicht gibt,
      // und "Zurueck" fuehrt ins Leere: das Aufraeumen haengt als
      // `vorher` an diesem Schritt und ist bereits gelaufen — die
      // Uebungsbestellung, auf die die vorige Karte zeigt, ist weg.
      // Auch das Kreuz faellt weg; es wuerde die Rueckfrage "Die
      // Uebungsbestellung wird dabei entfernt" ausloesen, die dann nicht
      // mehr stimmt. Weggenommen werden die beiden in blaseAufbereiten().
      nextBtnText: letzterKnopf(s, i === schritte.length - 1)
    }
  }));

  async function gehZu(neu: number): Promise<void> {
    if (beendet) return;
    pulsStoppen();

    // Über den letzten Schritt hinaus heißt: durchgelaufen. Das Aufräumen
    // hängt als `vorher` am Schluss-Schritt und ist hier schon gelaufen.
    if (neu >= schritte.length) {
      beenden(true);
      return;
    }
    if (neu < 0) return;

    index = neu;
    const schritt = schritte[index];

    if (schritt.vorher !== undefined) {
      try {
        await umgebung.aktion(schritt.vorher);
      } catch (fehler) {
        console.warn('[Tour] Schritt übersprungen, Vorbereitung fehlgeschlagen:', fehler);
        void gehZu(index + 1);
        return;
      }
    }
    if (beendet) return;

    // Laufzeit-Netz: fehlt der Anker trotz allem, wird der Schritt
    // übersprungen statt die Tour steckenzulassen. Die Warnung nennt den
    // Anker — damit ist die kaputte Stelle ohne Suchen zu finden.
    if (schritt.anker !== undefined) {
      const el = await warteAufAnker(schritt.anker, WARTE_AUF_ANKER_MS);
      if (beendet) return;
      if (el === null) {
        console.warn(
          `[Tour] Anker "${schritt.anker}" nicht gefunden — Schritt "${schritt.titel}" übersprungen.`
        );
        void gehZu(index + 1);
        return;
      }
    }

    // Erst an die richtige Stelle scrollen, DANN zeigen. Sonst zeichnet
    // driver.js den Ausschnitt an die Stelle, an der das Element vor dem
    // Scrollen stand, und die Sprechblase springt dem Blick hinterher
    // (B98). Der Abstand nach oben ist bewusst grosszuegig: darunter
    // klappen Menues nach oben auf und geraten in die Sprechblase (B93).
    if (schritt.anker !== undefined) {
      await scrolleHeran(ankerElement(schritt.anker));
      if (beendet) return;
    }

    // React tauscht Knoten aus; driver.js entfernt seine Markierung dann
    // am abgehängten Knoten und der sichtbare behält sie. Übrig bleiben
    // Elemente, die weiter anklickbar sind, obwohl der Scheinwerfer
    // längst woanders steht. Deshalb vorher aufräumen.
    document
      .querySelectorAll('.driver-active-element')
      .forEach((el) => el.classList.remove('driver-active-element'));

    // Der erste Schritt MUSS über drive() laufen, nicht über moveTo():
    // drive() richtet driver.js überhaupt erst ein und setzt dabei die
    // Klasse `driver-active` auf den Body. Ohne sie greifen die eigenen
    // Regeln der Bibliothek nicht — und der Overlay bleibt auf
    // pointer-events:auto stehen und schluckt jeden Klick, auch den auf
    // das beleuchtete Element. Beim Nachmessen im Build genau so
    // aufgefallen: der Knopf war zu sehen und nicht zu treffen.
    imWechsel = true;
    if (fuehrer?.isActive() === true) {
      fuehrer.moveTo(index);
    } else {
      fuehrer?.drive(index);
    }

    // Reihenfolge eines Schrittwechsels, bewusst starr:
    //   Abdunklung steht (der Overlay wird nie abgebaut)
    //   → Scrollen ist abgeschlossen (oben)
    //   → Ausschnitt und Sprechblase EINMAL gesetzt (moveTo, animate:false)
    //   → zwei Bilder warten, damit die Platzierung im Layout steht
    //   → einblenden, danach bewegt sich nichts mehr
    //   → erst jetzt Puls und Beobachter scharf schalten
    await zweiBilder();
    if (beendet) {
      imWechsel = false;
      return;
    }
    blaseZeigen();
    pulsStarten();
  }

  function pulsStarten(): void {
    const schritt = schritte[index];
    // Mit der JETZIGEN Lage starten, nicht mit einem leeren Wert: sonst
    // meldet der erste Tick eine Veraenderung, die es nie gab, und fuehrt
    // unmittelbar nach dem Erscheinen ein zweites Mal nach.
    letzteLage = lageVon(fuehrer?.getActiveElement());
    // Scharf erst, wenn die Einblendung durch ist.
    ruheBis = performance.now() + RUHEFENSTER_MS;

    // Der Puls merkt eine Verschiebung erst beim naechsten Tick. Waechst
    // der markierte Bereich direkt nach dem Erscheinen — die Zeile fuer
    // das Abholdatum klappt auf, eine Sendung kommt dazu —, steht der
    // Ausschnitt bis dahin falsch und deckt zu, was angeklickt werden
    // soll (B91). Der Beobachter meldet das im selben Bild.
    const ziel = fuehrer?.getActiveElement();
    if (ziel instanceof HTMLElement && typeof ResizeObserver !== 'undefined') {
      beobachter = new ResizeObserver(() => nachfuehren());
      beobachter.observe(ziel);
    }

    puls = window.setInterval(() => {
      if (beendet) return;
      nachfuehren();
      if (schritt.erwartung === undefined) return;
      if (erwartungErfuellt(schritt.erwartung, umgebung.pfad())) {
        void gehZu(index + 1);
      }
    }, PULS_MS);

    /**
     * Hält den Scheinwerfer auf dem Zielelement.
     *
     * Nötig, weil sich die Seite während eines Mitmach-Schritts ändert —
     * eine Sendung kommt dazu, eine Zeile wächst, alles darunter rutscht.
     * Der Ausschnitt blieb dann stehen, wo das Element vorher war, und
     * die Sperre lag über dem, was angeklickt werden sollte.
     *
     * refresh() zieht den Fokus in die Sprechblase. Deshalb NICHT, solange
     * jemand im beleuchteten Bereich tippt: sonst verliert das Textfeld
     * für die Kommunikationsnotiz mitten im Satz den Fokus.
     */
    function nachfuehren(): void {
      // Ruhefenster: solange die Sprechblase einblendet, wird nichts
      // nachgeführt. Ein refresh() in diesem Moment würde sie ein zweites
      // Mal setzen — und genau das sah man als Springen (Befund B).
      if (performance.now() < ruheBis) return;
      const aktiv = fuehrer?.getActiveElement();
      if (aktiv === undefined) return;
      if (!document.body.contains(aktiv)) {
        fuehrer?.refresh();
        return;
      }
      const lage = lageVon(aktiv);
      if (lage === letzteLage) return;
      letzteLage = lage;
      if (!wirdGetippt(aktiv)) fuehrer?.refresh();
    }
  }

  fuehrer = driver({
    steps: driverSchritte,
    // Ohne Wanderung des Ausschnitts. Die 400-ms-Animation von driver.js
    // rechnet den Scheinwerfer Bild für Bild vom alten zum neuen Ziel und
    // baut die Sprechblase auf halber Strecke ein zweites Mal auf. Damit
    // laufen zwei Zyklen gegeneinander — der eigene (verstecken, setzen,
    // einblenden) und der der Bibliothek. Der Ausschnitt springt jetzt in
    // einem Bild um, die Abdunklung steht dabei durchgehend.
    animate: false,
    allowClose: true,
    // Kein Zufallsklick soll die Tour beenden: schließen geht über das
    // Kreuz. Und die Tastatur gehört der Anwendung — Esc verwirft in der
    // Erfassung einen ISBN-Treffer, das darf die Tour nicht schlucken.
    overlayClickBehavior: () => {},
    allowKeyboardControl: false,
    disableActiveInteraction: false,
    smoothScroll: true,
    stagePadding: 6,
    stageRadius: 8,
    overlayColor: '#2B2B2B',
    overlayOpacity: 0.55,
    popoverClass: 'engel-tour',
    showProgress: true,
    progressText: 'Schritt {{current}} von {{total}}',
    nextBtnText: 'Weiter',
    prevBtnText: 'Zurück',
    doneBtnText: 'Fertig',
    showButtons: ['next', 'previous', 'close'],
    onNextClick: () => void gehZu(index + 1),
    onPrevClick: () => void gehZu(index - 1),
    onCloseClick: () => {
      void umgebung.abbruchBestaetigen().then((ja) => {
        if (ja) beenden(false);
      });
    },
    onDestroyed: () => beenden(false)
  });

  void gehZu(0);

  return () => beenden(false);
}
